import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { DomHelper } from '@barthauer/abc/core';
import { Subject } from 'rxjs';
import { _AbcTooltipComponent } from '../abc-tooltip';

@Component({
  selector: 'abc-tile-nav',
  templateUrl: 'abc-tile-nav.component.html',
  styleUrls: ['abc-tile-nav.component.scss'],
  imports: [CommonModule],
})
export class _AbcTileNavComponent implements AfterViewInit, OnDestroy {
  @ViewChild('tileNavContainer') tileNavContainer: ElementRef<HTMLElement>;
  @ViewChild('flat') flat: ElementRef<HTMLElement>;
  @ViewChild('nested') nested: ElementRef<HTMLElement>;
  @ViewChild('menu') menu: ElementRef<HTMLElement>;
  @ViewChildren('captions') captions: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('nestedCaptions') nestedCaptions: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('nestedFirstCaption') nestedFirstCaption: ElementRef<HTMLElement>;
  @ViewChild('nestedLastCaption') nestedLastCaption: ElementRef<HTMLElement>;

  @Input() set dataSource(value: any[]) {
    this._dataSource = value;

    if (!this.displayExpr && value.length) {
      this.deduceExpr(value[0]);
    }
  }
  get dataSource(): any[] {
    return this._dataSource;
  }
  @Input() displayExpr: string;
  /** highlight currently active tile */
  @Input() activeStateEnabled: boolean = false;
  /** highlight currently hovered tile */
  @Input() hoverStateEnabled: boolean = false;
  /** use this when you have overlaying content that hides your tile nav menu component */
  @Input() fillContent: boolean = false;
  /** use this when you have overlaying content that hides your tile nav component */
  @Input() offset: number = 0;
  /** tiles rtlEnabled */
  @Input() set rtlEnabled(value: boolean) {
    this.direction = value ? 'rtl' : 'ltr';
  }
  /** function that determines if the tile is disabled */
  @Input() disabled: (item: any) => boolean | ((item: any) => Promise<boolean>) = (item: any) => {
    return this.dataSource[this.dataSource.length - 1] === item;
  };

  /** returns the data emitted by a tile click */
  @Output() OnTileClick = new EventEmitter<OnTileClickEvent>();

  private destroy$ = new Subject<void>();
  private resizeObserver: ResizeObserver;
  private _dataSource: any[] = [];
  private abcTileNavParentContainer: HTMLElement;
  private abcTileNavFlat: HTMLElement;

  protected direction: string = 'ltr';
  protected nestedView: boolean = false;
  protected menuOpen: boolean = false;
  protected menuDataSource: any[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource']) {
      if (this.dataSource.length >= 3) {
        this.menuDataSource = this.dataSource.slice(1, this.dataSource.length - 1);
      }

      setTimeout(() => {
        if (this.abcTileNavParentContainer) {
          this.switchView(this.abcTileNavParentContainer.clientWidth);
        }
      }, 0);

      return this.cdr.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    this.abcTileNavParentContainer = this.tileNavContainer.nativeElement.parentElement.parentElement;
    this.abcTileNavFlat = this.flat.nativeElement;

    if (this.abcTileNavParentContainer) {
      this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        for (const entry of entries) {
          const newWidth = entry.contentRect.width;
          this.switchView(newWidth);
          return this.cdr.detectChanges();
        }
      });

      this.resizeObserver.observe(this.abcTileNavParentContainer);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    document.removeEventListener('click', this.closeMenu);
  }

  /**
   * deduces the tile item display expression if no expression is given
   * @param item Object
   */
  private deduceExpr(item: Object) {
    const keys = Object.keys(item);
    keys.forEach((key) => {
      if (typeof item[key] === 'string') {
        this.displayExpr = key;
      }
    });
  }

  /**
   * switchs between flat and nested views and shows tooltip for overflow captions
   * the 50 raw number in the function is the space taken by the ... button in the nested view
   * @param parentWidth the parent component width hosting the tile-nav-component
   */
  private switchView(parentWidth: number): void {
    this.nestedView = this.abcTileNavFlat.scrollWidth - (parentWidth - this.offset) > 1 && this.dataSource.length >= 3;

    if (this.nestedView) {
      // for nested view
      const firstCaption = this.nestedFirstCaption.nativeElement;
      const lastCaption = this.nestedLastCaption.nativeElement;
      this.nestedLastCaption.nativeElement.style.maxWidth = `${
        parentWidth - firstCaption.clientWidth - 50 - this.offset
      }px`;
      this.toggleToolTip(lastCaption, 'tile-nav-');
    } else {
      // for flat view
      if (this.captions.length) {
        const lastCaption = this.captions.get(this.captions.length - 1).nativeElement;
        lastCaption.style.maxWidth = `${parentWidth - this.calcTakenSpace() - this.offset}px`;
        this.toggleToolTip(lastCaption, 'tile-nav-');
      }
    }
  }

  /**
   * the value 30 in the function describes the width of the arrow icon
   * @returns taken space inside the parent container by the previous tiles except the last one
   */
  private calcTakenSpace(): number {
    return this.captions.reduce((sum: number, caption: ElementRef<HTMLElement>, index: number) => {
      return index < this.captions.length - 1 ? (sum += caption.nativeElement.clientWidth + 30) : sum;
    }, 0);
  }

  /**
   * toggles a tooltip if the element has an overflowing text
   * @param captionElement the caption element
   * @param selector the selector id for the tooltip element
   */
  private toggleToolTip(captionElement: HTMLElement, selector: string): void {
    const targetTooltip = document.getElementById(selector + captionElement.innerText);
    if (captionElement.scrollWidth - captionElement.clientWidth > 1) {
      if (!targetTooltip) {
        this.createToolTip(captionElement, selector);
      }
    } else {
      if (targetTooltip) {
        this.removeToolTip(selector + captionElement.innerText);
      }
    }
  }

  /**
   * create a tooltip component in a hosting parent element
   * @param parentElement the parent element that hosts the tooltip
   * @param selector the selector id for the tooltip element
   */
  private createToolTip(parentElement: HTMLElement, selector: string) {
    const tooltip = this.viewContainerRef.createComponent(_AbcTooltipComponent);
    tooltip.instance.contentTemplate = parentElement.innerText;
    tooltip.location.nativeElement['id'] = selector + parentElement.innerText;
    tooltip.instance.target = parentElement;
    tooltip.instance.showEvent = 'mouseenter';
    tooltip.instance.hideEvent = 'mouseleave';
  }

  /**
   * removes a tooltip component using its id
   * @param id the tooltip component id
   */
  private removeToolTip(id: string) {
    const tooltip = document.getElementById(id);
    tooltip?.remove();
  }

  /**
   * calculates the item caption from a given tile nav item
   * @param item the tile nav item
   * @returns string
   */
  protected getItemCaption = (item: any): string => {
    if (!item) return 'noCaption';

    if (typeof item === 'string') return item;

    if (item[this.displayExpr]) return item[this.displayExpr];

    if (typeof item === 'object') {
      for (const key of Object.keys(item)) {
        const result = this.getItemCaption(item[key]);
        if (result !== 'noCaption') return result;
      }
    }

    return 'noCaption';
  };

  /**
   * deduces the className for a given item
   * @param item the tile-nav item
   * @returns the className
   */
  protected getItemState(item: any): string {
    const isActive = this.activeStateEnabled && item === this.dataSource[this.dataSource.length - 1];
    const isDisabled = this.disabled(item);
    const isHoverable = this.hoverStateEnabled && item !== this.dataSource[this.dataSource.length - 1];

    return [isActive ? 'active' : '', isDisabled ? 'disabled' : '', isHoverable ? 'hover' : '']
      .filter(Boolean)
      .join(' ');
  }

  /**
   * raises the event of a tile click
   * @param item the tile-nav item
   */
  protected onTileClick(item: any): void {
    const index = this.dataSource.indexOf(item);
    if (!this.disabled(item)) {
      this.OnTileClick.emit({
        index: index,
        data: this.dataSource[index],
      });
    }
  }

  /**
   * opens the nested tile-nav-menu
   */
  protected openMenu(): void {
    this.menuOpen = true;
    document.addEventListener('click', this.closeMenu);

    setTimeout(() => {
      if (this.menu) {
        this.menu.nativeElement.style.zIndex = `${DomHelper.getMaxZIndex()}`;
        this.nestedCaptions.forEach((caption: ElementRef<HTMLElement>) => {
          this.toggleToolTip(caption.nativeElement, 'tile-nav-menu-');
        });
      }
    }, 0);
  }

  /**
   * closes the nested tile-nav-menu
   */
  protected closeMenu = (e: MouseEvent): void => {
    if ((e.target as HTMLElement).id !== 'menu-toggler') {
      const tooltipCaptions = this.nestedCaptions.map((caption: ElementRef<HTMLElement>) => {
        return caption.nativeElement.innerText;
      });

      this.menuOpen = false;

      tooltipCaptions.forEach((caption) => {
        this.removeToolTip('tile-nav-menu-' + caption);
      });

      return this.cdr.detectChanges();
    }
  };
}

export interface OnTileClickEvent {
  index: number;
  data: any;
}
